import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, ChevronRight, Globe } from 'lucide-react';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import axiosInstance from '../../utils/axiosConfig';
import { foodbookInstance } from '../../utils/axiosConfig'; 
import PropTypes from 'prop-types'

const ProductModal = ({ isOpen, onClose, onSave }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [ownProducts, setOwnProducts] = useState([]);
    const [psProducts, setPsProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const handleFoodbook = async (e, product) => { 
        e.preventDefault();
        e.stopPropagation();

        window.open(`https://psinfoodservice.com/product/${product.id}-/`, '_blank'); //${encodeURIComponent(product.name)}
    }

    const handleProductSelect = async (product, type) => {
        try {
            setSearchTerm('');
            setOwnProducts([]);
            setPsProducts([]);
             
            var returnProduct =
            {
                allergens: [],
                description: '',
                type: type,
                footprint: 0,
                components: [],
                id: product.id,
                name: product.name,
                refid: product.id
            };

            onSave(returnProduct, type);
            onClose();
        } catch (error) {
            console.error('Error adding product:', error);
        }
    };

    const handleSearch = async () => {
        if (!searchTerm) return;
        setIsLoading(true);
        try {
            const [ownResponse, psResponse] = await Promise.all([
                axiosInstance.get(`/product/search?keyword=${searchTerm}`),
                foodbookInstance.post('/MenuApp/SearchResults', {
                    keyword: searchTerm,
                    filters: [],
                    pageIndex: 0,
                    pageSize: 200
                })
            ]);
            setOwnProducts(ownResponse.data);
            setPsProducts(psResponse.data);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg w-full max-w-7xl p-6 relative max-h-[80vh] min-h-[40vh] overflow-hidden flex flex-col">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
                >
                    <X size={24} />
                </button>

                <h2 className="text-xl font-inter text-ps-blue-dark mb-4">
                    {t('modals.addProduct')}
                </h2>

                <div className="relative mb-4">
                    <Input
                        type="text"
                        placeholder={t('modals.searchProduct')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    <Button
                        onClick={handleSearch}
                        className="inline-flex absolute right-0 top-0 items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white px-8 py-2 rounded"
                    >
                        <span>{t('products.search')}</span>
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-6">
                    {isLoading ? (
                        <div className="text-center py-4">{t('common.loading')}</div>
                    ) : (
                        <>
                            <section>
                                <h3 className="font-inter text-ps-blue-dark mb-2">{t('products.yourProducts')}</h3>
                                    <div className="space-y-2">
                                        {ownProducts.length > 0 ? (<>
                                            {ownProducts.map((product) => (
                                                <div
                                                    key={product.id}
                                                    onClick={() => handleProductSelect(product, 'Own')}
                                                    className="flex items-center justify-between w-full p-4 hover:pl-6 cursor-pointer border-b-[1px]"
                                                >
                                                    <span className="font-inter">{product.name}</span>
                                                    <div className="flex items-center gap-4">
                                                        <ChevronRight className="text-ps-blue-dark" size={20} />
                                                    </div>
                                                </div>
                                            ))
                                            }
                                        </>) : (
                                            <>...</>
                                        )}
                                </div>
                            </section>

                            <section>
                                <h3 className="font-inter text-ps-blue-dark mb-2">{t('products.psFoodserviceStock')}</h3>
                                    <div className="space-y-2">
                                        {psProducts?.products?.length > 0 ? (<>
                                            {psProducts.products?.map((product) => (
                                                <div
                                                    key={product.id}
                                                    onClick={() => handleProductSelect(product, 'Ps')}
                                                    className="flex items-center justify-between w-full p-4 hover:pl-6 cursor-pointer border-b-[1px]"
                                                >
                                                    <span className="font-inter">{product.name}</span>
                                                    <div className="flex items-center gap-4">
                                                        {product.brand !== 'Vers' && (
                                                            <Globe className="text-ps-blue-dark" size={20} onClick={(e) => handleFoodbook(e, product)} />
                                                        )}
                                                        <ChevronRight className="text-ps-blue-dark" size={20} />
                                                    </div>
                                                </div>
                                            ))}
                                        </>) : (
                                            <>...</>
                                        )}
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

ProductModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired
};

export default ProductModal;